import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck2,
  CalendarPlus,
  ClipboardCheck,
  Route,
  Search,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import { httpServices as services } from "@/services";
import type { FieldVisit, VisitPlan } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type ViewMode = "PLANS" | "VISITS";

export default function VisitsPage() {
  const [plans, setPlans] = useState<VisitPlan[]>();
  const [visits, setVisits] = useState<FieldVisit[]>();
  const [mode, setMode] = useState<ViewMode>("PLANS");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([
      services.visits.listPlans(),
      services.visits.list({ pageSize: 100 }),
    ])
      .then(([planRows, visitResult]) => {
        if (!active) return;
        setPlans(planRows);
        setVisits(visitResult.items);
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message || "Field Visit workspace could not be loaded.");
      });
    return () => { active = false; };
  }, []);

  const planRows = useMemo(
    () => (plans || []).filter((plan) =>
      `${plan.planCode} ${plan.title} ${plan.assignedStaffName || ""} ${plan.status}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    ),
    [plans, search],
  );

  const visitRows = useMemo(
    () => (visits || []).filter((visit) =>
      `${visit.reference} ${visit.farmerName} ${visit.farmName} ${visit.assignedStaffName} ${visit.conductedByStaffName || ""} ${visit.status}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    ),
    [visits, search],
  );

  if (error) return <ErrorState message={error} />;
  if (!plans || !visits) return <LoadingState label="Loading Field Visit management" />;

  const activePlans = plans.filter((plan) => ["SANCTIONED", "ACKNOWLEDGED", "IN_PROGRESS"].includes(plan.status)).length;
  const plannedFarmers = plans.reduce((sum, plan) => sum + plan.targetCount, 0);
  const visitedTargets = plans.reduce((sum, plan) => sum + plan.visitedCount, 0);
  const openFollowUps = visits.reduce((sum, visit) => sum + (visit.followUpCount || 0), 0);

  return (
    <div className="space-y-6" data-testid="visits-page">
      <PageHeader
        eyebrow="Farms & Agronomy"
        title="Field Visits"
        description="Plan and sanction field work, track acknowledgement and completion, and retain Plot-level evidence for every actual visit."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" asChild data-testid="visits-unscheduled-button">
              <Link to="/visits/unscheduled/start">
                <ShieldCheck className="mr-2 size-4" />
                Official unscheduled visit
              </Link>
            </Button>
            <Button asChild data-testid="visit-plan-new-button">
              <Link to="/visits/plans/new">
                <CalendarPlus className="mr-2 size-4" />
                New Visit Plan
              </Link>
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Active plans</p><p className="mt-2 text-3xl font-semibold">{activePlans}</p></div>
              <CalendarCheck2 className="size-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Farmers scheduled</p><p className="mt-2 text-3xl font-semibold">{plannedFarmers}</p></div>
              <Route className="size-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Targets visited</p><p className="mt-2 text-3xl font-semibold">{visitedTargets}</p></div>
              <UserCheck className="size-5 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Follow-ups created</p><p className="mt-2 text-3xl font-semibold">{openFollowUps}</p></div>
              <ClipboardCheck className="size-5 text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border bg-card p-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex rounded-xl bg-muted/60 p-1">
          <Button
            type="button"
            size="sm"
            variant={mode === "PLANS" ? "default" : "ghost"}
            onClick={() => setMode("PLANS")}
            data-testid="visit-view-plans"
          >
            Plans & Schedules
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "VISITS" ? "default" : "ghost"}
            onClick={() => setMode("VISITS")}
            data-testid="visit-view-actual"
          >
            Actual Visits
          </Button>
        </div>
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
            className="pl-9"
            placeholder={mode === "PLANS" ? "Search plan, staff, or status" : "Search visit, farmer, farm, or staff"}
            data-testid="visits-search-input"
          />
        </div>
      </div>

      {mode === "PLANS" ? (
        <Card data-testid="visit-plans-panel">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Visit Plan</TableHead>
                    <TableHead>Assigned staff</TableHead>
                    <TableHead>Date range</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Missed</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {planRows.map((plan) => (
                    <TableRow key={plan.id} className="data-table-row">
                      <TableCell>
                        <Link to={`/visits/plans/${plan.id}`} className="font-semibold text-primary hover:underline">
                          {plan.title}
                        </Link>
                        <p className="text-xs text-muted-foreground">{plan.planCode}</p>
                      </TableCell>
                      <TableCell>{plan.assignedStaffName || "Not resolved"}</TableCell>
                      <TableCell className="text-sm">
                        {plan.plannedStartDate}{plan.plannedEndDate !== plan.plannedStartDate ? ` → ${plan.plannedEndDate}` : ""}
                      </TableCell>
                      <TableCell>
                        <p className="font-semibold">{plan.visitedCount} / {plan.targetCount}</p>
                        <p className="text-xs text-muted-foreground">{plan.remainingCount} remaining</p>
                      </TableCell>
                      <TableCell>{plan.missedCount}</TableCell>
                      <TableCell><StatusBadge status={plan.status} testId={`visit-plan-status-${plan.id}`} /></TableCell>
                    </TableRow>
                  ))}
                  {planRows.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No Visit Plans match this view.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="actual-visits-panel">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Visit</TableHead>
                    <TableHead>Farmer / Farm / Plots</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead>Conducted by</TableHead>
                    <TableHead>Evidence</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visitRows.map((visit) => (
                    <TableRow key={visit.id} className="data-table-row">
                      <TableCell>
                        <Link to={`/visits/${visit.id}`} className="font-semibold text-primary hover:underline">
                          {visit.reference}
                        </Link>
                        <p className="text-xs text-muted-foreground">{visit.visitDate} · {visit.visitOrigin || "LEGACY"}</p>
                      </TableCell>
                      <TableCell>
                        {visit.farmerName}
                        <p className="text-xs text-muted-foreground">
                          {visit.farmName}
                          {visit.plotNames?.length ? ` · ${visit.plotNames.join(", ")}` : (visit.plotName ? ` · ${visit.plotName}` : "")}
                        </p>
                      </TableCell>
                      <TableCell>{visit.assignedStaffName}</TableCell>
                      <TableCell>{visit.conductedByStaffName || "Not checked in"}</TableCell>
                      <TableCell>
                        <p className="font-semibold">{visit.evidenceCount || 0} photos</p>
                        <p className="text-xs text-muted-foreground">{visit.activityCount || 0} activities · {visit.followUpCount || 0} follow-ups</p>
                      </TableCell>
                      <TableCell><StatusBadge status={visit.status} testId={`visit-status-${visit.id}`} /></TableCell>
                    </TableRow>
                  ))}
                  {visitRows.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No actual visits match this view.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
